import React, { useState, useMemo } from 'react';
import Card from 'lib/components/Card/Card';
import { Permissions } from 'lib/permissions/roles';
import { TableLayout } from 'lib/components/TableLayout';
import { formatPriceWithCentsAndCommas } from 'lib/pricing/ui';
import { Alert } from 'lib/components/Alert';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { ColumnService } from 'lib/services/directory';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { ColumnButton } from 'lib/components/ColumnButton';
import {
  ReportsAgingWindowData,
  ReportsAgingWindowKeyType
} from 'lib/enums/Reports/AccountingTabEnums';
import { logAndCaptureException } from 'utils';
import { mapAsync } from 'lib/helpers/array/async';
import { safeGetOrThrow } from 'lib/safeWrappers';
import { wrapSuccess, wrapError, ResponseOrError } from 'lib/types/responses';
import { SearchableInvoiceRecord } from 'lib/types/searchable';
import { AccountingTableRowData } from 'lib/types/reports/AccountingTableRows';
import { getFirebaseContext } from 'utils/firebase';
import { ReportHeader } from '../components/ReportHeader';
import {
  INVOICE_CSV_COLUMNS,
  REPORT_CURRENCY_SYMBOL
} from '../constants/csvColumns';
import { useReportsLoading } from '../context/LoadingContext';
import { useReportParams } from '../context/ReportParamsContext';
import {
  fetchInvoicesByAgingRowsForReports,
  fetchUnpaidInvoicesFromElastic
} from '../utils/elastic';
import { Multiselect } from '../components/Multiselect';
import {
  exportSearchableRecordsToCSV,
  SearchableRecordWithAdditionalFields
} from '../utils/exportOrdersToCSV';

enum SortBy {
  None = 'none',
  TotalOverdueAmount = 'totalOverdueAmount',
  NumberOfOverdueInvoices = 'numberOfOverdueInvoices'
}

enum SortDirection {
  Asc = 'asc',
  Desc = 'desc'
}

const handleExportCsv = async (
  allowedOrgIds: string[],
  row: AccountingTableRowData | null
): Promise<void> => {
  let params: { filerId?: string; filerOrgId?: string } | undefined;

  // Search on filerOrgId if available, otherwise search on filerId
  if (row?.filerOrgId) {
    params = { filerOrgId: row.filerOrgId };
  } else if (row?.filerId) {
    params = { filerId: row.filerId };
  }

  const { response: invoices, error } = await fetchUnpaidInvoicesFromElastic(
    allowedOrgIds,
    params
  );

  if (error) {
    logAndCaptureException(
      ColumnService.PAYMENTS,
      error,
      'Error fetching unpaid invoices',
      { rowData: JSON.stringify(row), params: JSON.stringify(params) }
    );
    return;
  }

  const [errGettingInvoicesWithAffidavits, invoicesWithAffidavits] =
    await mapAsync(
      invoices,
      async (
        invoice: SearchableInvoiceRecord
      ): Promise<ResponseOrError<SearchableRecordWithAdditionalFields>> => {
        const { noticeid } = invoice;
        if (!noticeid) {
          logAndCaptureException(
            ColumnService.PAYMENTS,
            new Error(
              'Notice ID is required to populate CSV export in Reports'
            ),
            'Error fetching notice for invoice to populate CSV export in Reports',
            { invoiceId: invoice.id }
          );
          // don't block the export, but this is clearly a data integrity issue so we should log the exception
          return wrapSuccess({ ...invoice, affidavitURL: '', noticeTitle: '' });
        }
        const [noticeErr, notice] = await safeGetOrThrow(
          getFirebaseContext().userNoticesRef().doc(noticeid)
        );

        if (noticeErr) {
          logAndCaptureException(
            ColumnService.PAYMENTS,
            noticeErr,
            'Error fetching notice for invoice to populate CSV export in Reports',
            { invoiceId: invoice.id }
          );
          return wrapError(noticeErr);
        }

        const { referenceId, affidavitURL } = notice.data();

        return wrapSuccess({
          ...invoice,
          affidavitURL: affidavitURL || '',
          noticeTitle: referenceId || ''
        });
      }
    );

  if (errGettingInvoicesWithAffidavits) {
    logAndCaptureException(
      ColumnService.PAYMENTS,
      errGettingInvoicesWithAffidavits,
      'Error getting invoices with affidavits to populate CSV export in Reports'
    );
    return;
  }

  const filename = `invoices_${
    row
      ? row?.billingName.replace(/[^a-zA-Z0-9]/g, '_')
      : allowedOrgIds.map(id => id.slice(0, 5)).join('_')
  }.csv`;

  exportSearchableRecordsToCSV({
    dataAndColumns: [
      {
        data: invoicesWithAffidavits,
        columns: INVOICE_CSV_COLUMNS
      }
    ],
    filename
  });
};

export { SortBy as ReportsAccountingTabSortBy };
export { SortDirection as ReportsAccountingTabSortDirection };

export function AccountingTab() {
  const { getOrgIdsWithPermission, getOrgIds } = useReportParams();
  const { withLoading } = useReportsLoading();

  const orgIds = getOrgIds();
  const allowedOrgIds = getOrgIdsWithPermission(Permissions.PAYMENTS_REPORT);
  const [sortBy, setSortBy] = useState<SortBy | ReportsAgingWindowKeyType>(
    SortBy.None
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.Desc
  );
  const [selectedAgingPeriods, setSelectedAgingPeriods] = useState<string[]>(
    []
  );

  const handleSort = (column: SortBy | ReportsAgingWindowKeyType) => {
    if (sortBy === column) {
      setSortDirection(
        sortDirection === SortDirection.Asc
          ? SortDirection.Desc
          : SortDirection.Asc
      );
    } else {
      setSortBy(column);
      setSortDirection(SortDirection.Desc);
    }
  };

  const {
    value: fetchedData,
    isLoading,
    error
  } = useAsyncEffect<AccountingTableRowData[] | null>({
    fetchData: async () =>
      withLoading(fetchInvoicesByAgingRowsForReports(allowedOrgIds)),
    initialData: null,
    dependencies: [orgIds.sort().join(',')],
    errorConfig: {
      service: ColumnService.ELASTIC,
      message:
        '[COLUMN REPORTS] Failed to fetch customer data for Customers tab',
      tags: {
        orgsFilter: orgIds.join(', ')
      }
    }
  });

  const agingWindows: Array<{
    id: string;
    label: string;
  }> = Object.values(ReportsAgingWindowData).map(({ label, key }) => ({
    id: key,
    label
  }));

  const agingPeriodOptions = agingWindows.map(window => ({
    value: window.id,
    label: window.label
  }));

  const rows: AccountingTableRowData[] = useMemo(() => {
    if (!fetchedData || !fetchedData.length) {
      return [];
    }

    const selectedPeriodsSet = new Set(selectedAgingPeriods);

    const filteredRows =
      selectedAgingPeriods.length > 0
        ? fetchedData.filter(row => {
            for (const [period, amount] of Object.entries(row.agingWindows)) {
              if (selectedPeriodsSet.has(period) && amount > 0) {
                return true;
              }
            }
            return false;
          })
        : fetchedData;

    if (sortBy !== SortBy.None) {
      return filteredRows.sort((a, b) => {
        let aValue: number;
        let bValue: number;

        if (sortBy === SortBy.TotalOverdueAmount) {
          aValue = a.totalOverdueAmount;
          bValue = b.totalOverdueAmount;
        } else if (sortBy === SortBy.NumberOfOverdueInvoices) {
          aValue = a.numberOfOverdueInvoices;
          bValue = b.numberOfOverdueInvoices;
        } else {
          aValue = a.agingWindows[sortBy as ReportsAgingWindowKeyType] || 0;
          bValue = b.agingWindows[sortBy as ReportsAgingWindowKeyType] || 0;
        }

        if (sortDirection === SortDirection.Asc) {
          return aValue - bValue;
        }
        return bValue - aValue;
      });
    }

    return filteredRows;
  }, [fetchedData, agingWindows, sortBy, sortDirection, selectedAgingPeriods]);

  const handleExportClick = async (row: AccountingTableRowData) => {
    await handleExportCsv(allowedOrgIds, row);
  };

  const handleExportAllClick = async () => {
    await handleExportCsv(allowedOrgIds, null);
  };

  const handleAgingPeriodsChange = (selectedPeriods: string[]) => {
    setSelectedAgingPeriods(selectedPeriods);
  };

  const headerComponents = {
    totalOverdueAmount: (
      <th
        key={SortBy.TotalOverdueAmount}
        className="font-medium cursor-pointer select-none"
        onClick={() => handleSort(SortBy.TotalOverdueAmount)}
        style={{ userSelect: 'none' }}
      >
        <div className="flex items-center gap-1 font-medium">
          Total Overdue Amount
          {sortBy === SortBy.TotalOverdueAmount &&
            sortDirection === SortDirection.Desc && (
              <ChevronDownIcon className="w-4 h-4 ml-1" />
            )}
          {sortBy === SortBy.TotalOverdueAmount &&
            sortDirection === SortDirection.Asc && (
              <ChevronUpIcon className="w-4 h-4 ml-1" />
            )}
          {sortBy !== SortBy.TotalOverdueAmount && (
            <ChevronDownIcon className="w-4 h-4 ml-1 text-column-gray-300" />
          )}
        </div>
      </th>
    ),
    numberOfOverdueInvoices: (
      <th
        key={SortBy.NumberOfOverdueInvoices}
        className="font-medium cursor-pointer select-none"
        onClick={() => handleSort(SortBy.NumberOfOverdueInvoices)}
        style={{ userSelect: 'none' }}
      >
        <div className="flex items-center gap-1 font-medium">
          # Overdue Invoices
          {sortBy === SortBy.NumberOfOverdueInvoices &&
            sortDirection === SortDirection.Desc && (
              <ChevronDownIcon className="w-4 h-4 ml-1" />
            )}
          {sortBy === SortBy.NumberOfOverdueInvoices &&
            sortDirection === SortDirection.Asc && (
              <ChevronUpIcon className="w-4 h-4 ml-1" />
            )}
          {sortBy !== SortBy.NumberOfOverdueInvoices && (
            <ChevronDownIcon className="w-4 h-4 ml-1 text-column-gray-300" />
          )}
        </div>
      </th>
    ),
    export: (
      <th key="export" className="font-medium">
        Export CSV
      </th>
    ),
    agingWindows: agingWindows.map((window: { id: string; label: string }) => (
      <th
        key={window.id}
        className="font-medium cursor-pointer select-none"
        onClick={() => handleSort(window.id as ReportsAgingWindowKeyType)}
      >
        <div className="flex items-center gap-1 font-medium">
          {window.label}
        </div>
      </th>
    ))
  };

  const columns = [
    'Billing Name',
    'Filer Email',
    headerComponents.numberOfOverdueInvoices,
    ...headerComponents.agingWindows,
    headerComponents.totalOverdueAmount,
    headerComponents.export
  ];

  return (
    <Card>
      <ReportHeader
        title="Accounting"
        subtitle="View unpaid notice invoices by customer"
        permission={Permissions.PAYMENTS_REPORT}
        hideDateRangePicker
      />
      <div className="p-6">
        {error && (
          <Alert status="error" id="customers-tab-error">
            Error fetching accounting data: {error.message}
          </Alert>
        )}
        <TableLayout
          columns={columns}
          data={rows}
          id="customers-table"
          loading={isLoading}
          header={{
            title: '',
            subtitle: ''
          }}
          actionable={
            <div className="flex items-center gap-3">
              <div className="w-64">
                <Multiselect
                  value={selectedAgingPeriods}
                  onChange={handleAgingPeriodsChange}
                  options={agingPeriodOptions}
                  placeholder="Select aging periods..."
                  labelText=""
                />
              </div>
              <ColumnButton
                tertiary
                size="sm"
                onClick={handleExportAllClick}
                startIcon={<ArrowDownTrayIcon className="w-5 h-5" />}
                type="button"
                buttonText="Export All"
              />
            </div>
          }
          filterable={{
            shouldShowTableItem: (
              item: AccountingTableRowData,
              search: string
            ) => item.billingName.toLowerCase().includes(search.toLowerCase()),
            searchEnabled: true
          }}
          renderRow={(row: AccountingTableRowData) => (
            <>
              <td className="px-2">{row.billingName}</td>
              <td className="px-2">{row.filerEmail}</td>
              <td className="px-2">{row.numberOfOverdueInvoices}</td>
              {agingWindows.map(window => (
                <td key={window.id} className="px-2">
                  {formatPriceWithCentsAndCommas(
                    REPORT_CURRENCY_SYMBOL,
                    row.agingWindows[window.id as ReportsAgingWindowKeyType]
                  )}
                </td>
              ))}
              <td className="px-2">
                {formatPriceWithCentsAndCommas(
                  REPORT_CURRENCY_SYMBOL,
                  row.totalOverdueAmount
                )}
              </td>
              <td className="px-2">
                <ColumnButton
                  tertiary
                  size="sm"
                  onClick={() => handleExportClick(row)}
                  startIcon={<ArrowDownTrayIcon className="w-5 h-5" />}
                  type="button"
                  aria-label="Export CSV for customer"
                />
              </td>
            </>
          )}
        />
      </div>
    </Card>
  );
}
